import React from "react";
import { Paper, CircularProgress, Box, Typography } from "@mui/material";
import {
  SpecialLocation,
  GroupTotal,
  LocationTotal,
} from "@/pages/billet-yard-inventory/models";
import { Group } from "@/pages/billet-yard-inventory/types";
import { Location } from "../Location";

interface SpecialLocationsSectionProps {
  orderedSpecialLocations: SpecialLocation[];
  groupsByLocationId: Record<number, Group[]>;
  groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
  locationTotalsById: Record<number, LocationTotal>;
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  isLocationSearchHighlighted: (locationId: number) => boolean;
  getSearchMatchingGroupIds: (
    locationId: number,
    groupsByLocationId: Record<number, Group[]>,
  ) => number[];
  onLocationClick: (locationId: number) => void;
  onLocationHoldTap?: (locationId: number) => void;
  isSupervisor?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  hasData?: boolean;
}

export const SpecialLocationsSection = ({
  orderedSpecialLocations,
  groupsByLocationId,
  groupTotalsByLocationId,
  locationTotalsById,
  sourceLocationId,
  destinationLocationId,
  isLocationSearchHighlighted,
  getSearchMatchingGroupIds,
  onLocationClick,
  onLocationHoldTap,
  isSupervisor = false,
  isLoading = false,
  isRefreshing = false,
  hasData = true,
}: SpecialLocationsSectionProps) => {
  // Show loader when no special locations and we're loading (but page has loaded past main LoadingAndEmptyStates)
  const shouldShowLoader =
    orderedSpecialLocations.length === 0 && (isLoading || isRefreshing);

  return (
    <Paper
      elevation={2}
      sx={{
        flex: "1",
        height: "90%",
        overflowX: "auto",
        overflowY: "hidden",
        minWidth: 0,
        padding: "12px",
      }}
    >
      {shouldShowLoader ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="200px"
          p={2}
        >
          <CircularProgress />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginTop: "1rem" }}
          >
            Loading special locations...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            height: "100%",
            minWidth: `${Math.max(orderedSpecialLocations.length * 160, 800)}px`,
          }}
        >
          {orderedSpecialLocations.map((location, index) => {
            const groups = groupsByLocationId[location.LocationID] || [];

            // Group HOMELESS and BIN together like in old system
            if (location.MappingName === "HOMELESS_LOCATION_ID") {
              const binLocation = orderedSpecialLocations.find(
                (loc) => loc.MappingName === "BIN_LOCATION_ID",
              );
              const binGroups = binLocation
                ? groupsByLocationId[binLocation.LocationID] || []
                : [];

              return (
                <Box
                  key={`${location.LocationID}-group`}
                  sx={{
                    flex: "1 0 160px",
                    height: "100%",
                    minWidth: "160px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  {/* HOMELESS (top half) */}
                  <Box
                    sx={{
                      flex: "1",
                      minHeight: "48%",
                    }}
                  >
                    <Location
                      location={location}
                      groups={groups}
                      isSelected={
                        sourceLocationId === location.LocationID ||
                        destinationLocationId === location.LocationID
                      }
                      isSearchHighlighted={isLocationSearchHighlighted(
                        location.LocationID,
                      )}
                      searchMatchingGroupIds={getSearchMatchingGroupIds(
                        location.LocationID,
                        groupsByLocationId,
                      )}
                      groupTotalsByGroupName={
                        groupTotalsByLocationId[location.LocationID] || {}
                      }
                      locationTotal={locationTotalsById[location.LocationID]}
                      onClick={() => onLocationClick(location.LocationID)}
                      onHoldTap={
                        onLocationHoldTap &&
                        (isSupervisor || location.ClearEnabled)
                          ? () => onLocationHoldTap(location.LocationID)
                          : undefined
                      }
                    />
                  </Box>
                  {/* BIN (bottom half) */}
                  {binLocation && (
                    <Box
                      sx={{
                        flex: "1",
                        minHeight: "48%",
                      }}
                    >
                      <Location
                        location={binLocation}
                        groups={binGroups}
                        isSelected={
                          sourceLocationId === binLocation.LocationID ||
                          destinationLocationId === binLocation.LocationID
                        }
                        isSearchHighlighted={isLocationSearchHighlighted(
                          binLocation.LocationID,
                        )}
                        searchMatchingGroupIds={getSearchMatchingGroupIds(
                          binLocation.LocationID,
                          groupsByLocationId,
                        )}
                        groupTotalsByGroupName={
                          groupTotalsByLocationId[binLocation.LocationID] || {}
                        }
                        locationTotal={
                          locationTotalsById[binLocation.LocationID]
                        }
                        onClick={() => onLocationClick(binLocation.LocationID)}
                        onHoldTap={
                          onLocationHoldTap &&
                          (isSupervisor || binLocation.ClearEnabled)
                            ? () => onLocationHoldTap(binLocation.LocationID)
                            : undefined
                        }
                      />
                    </Box>
                  )}
                </Box>
              );
            }

            // Skip BIN since it's rendered with HOMELESS
            if (location.MappingName === "BIN_LOCATION_ID") {
              return null;
            }

            // Regular special location (full size)
            return (
              <Box
                key={location.LocationID}
                sx={{
                  flex: "1 0 160px",
                  height: "100%",
                  minWidth: "160px",
                }}
              >
                <Location
                  location={location}
                  groups={groups}
                  isSelected={
                    sourceLocationId === location.LocationID ||
                    destinationLocationId === location.LocationID
                  }
                  isSearchHighlighted={isLocationSearchHighlighted(
                    location.LocationID,
                  )}
                  searchMatchingGroupIds={getSearchMatchingGroupIds(
                    location.LocationID,
                    groupsByLocationId,
                  )}
                  groupTotalsByGroupName={
                    groupTotalsByLocationId[location.LocationID] || {}
                  }
                  locationTotal={locationTotalsById[location.LocationID]}
                  onClick={() => onLocationClick(location.LocationID)}
                  onHoldTap={
                    onLocationHoldTap && (isSupervisor || location.ClearEnabled)
                      ? () => onLocationHoldTap(location.LocationID)
                      : undefined
                  }
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};
